import { useState } from 'react'

import { G, beamShape, oscStep, ru } from '../../lib/physics'
import { ArrowDefs, Slider, useFrames } from './common'

/** Гиря на линейке: сила упругости растёт с прогибом, пока не уравновесит силу тяжести (урок 34). */

const SLOW = 5 // во сколько раз замедлено время: настоящие колебания линейки — доли секунды
const PX_PER_CM = 12
const PX_PER_N = 25 // стрелка 1 Н; длиннее 110 px не рисуем (уходит за рисунок)
const BASE = 110 // где лежит недеформированная линейка

export default function RulerLab() {
  const [massG, setMassG] = useState(300)
  const [k, setK] = useState(100)
  const [state, setState] = useState({ y: 0, v: 0 }) // прогиб, м; скорость, м/с

  const m = massG / 1000
  const fHeavy = m * G
  const yEq = fHeavy / k

  useFrames((dt) => {
    setState((s) => {
      let { y, v } = s
      const omega = Math.sqrt(k / m)
      const sub = 8
      for (let i = 0; i < sub; i++) [y, v] = oscStep(y, v, yEq, omega, 0.12, dt / SLOW / sub)
      return { y, v }
    })
  })

  const fEl = k * Math.max(state.y, 0)
  const ycPx = Math.min(state.y * 100 * PX_PER_CM, 70)
  const cx = 180
  const half = 120
  const точки: string[] = []
  for (let x = -140; x <= 140; x += 4) точки.push(`${cx + x},${BASE + beamShape(x, ycPx, half)}`)
  const верх = BASE + ycPx // верх линейки под гирей
  const гиряY = верх - 30
  const центр = гиряY + 15
  const стрелкаТяж = Math.min(fHeavy * PX_PER_N, 110)
  const стрелкаУпр = Math.min(fEl * PX_PER_N, 110)

  const движется = Math.abs(state.v) > yEq * 0.15 + 1e-4
  let подпись: string
  if (fEl < fHeavy * 0.97) {
    подпись = state.v >= 0 ? 'F упр < F тяж — гиря движется вниз с ускорением' : 'F упр < F тяж — подъём замедляется'
  } else if (fEl > fHeavy * 1.03) {
    подпись = state.v >= 0 ? 'F упр > F тяж — опускание замедляется' : 'F упр > F тяж — гиря поднимается'
  } else {
    подпись = движется ? 'Гиря проходит положение равновесия' : 'Равновесие: F упр = F тяж'
  }

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 290" role="img" aria-label="Гиря на линейке">
        <ArrowDefs />
        <line x1={30} y1={BASE} x2={330} y2={BASE} className="ink-muted" strokeDasharray="5 4" />
        <polyline points={точки.join(' ')} className="ink" strokeWidth={6} fill="none" strokeLinecap="round" />
        {[cx - half, cx + half].map((x) => (
          <polygon key={x} points={`${x},${BASE + 3} ${x - 12},${BASE + 28} ${x + 12},${BASE + 28}`} className="fill-muted" />
        ))}
        <rect x={cx - 22} y={гиряY} width={44} height={30} rx={3} className="fill-body ink" strokeWidth={1.5} />
        <text x={cx - 28} y={гиряY - 8} textAnchor="end" className="lab-label">{massG} г</text>
        <line x1={cx - 6} y1={центр} x2={cx - 6} y2={центр + стрелкаТяж} className="ink-heavy" strokeWidth={3} markerEnd="url(#head-heavy)" />
        <text x={cx - 14} y={центр + стрелкаТяж} textAnchor="end" className="lab-label heavy">F тяж</text>
        {стрелкаУпр > 4 && (
          <>
            <line x1={cx + 6} y1={верх} x2={cx + 6} y2={верх - стрелкаУпр} className="ink-el" strokeWidth={3} markerEnd="url(#head-el)" />
            <text x={cx + 14} y={верх - стрелкаУпр + 10} className="lab-label el">F упр</text>
          </>
        )}
      </svg>

      <div className="lab-caption">{подпись}</div>
      <div className="lab-readout">
        <span className="heavy">F тяж = m·g = {ru(fHeavy, 2)} Н</span>
        <span className="el">F упр = k·y = {ru(fEl, 2)} Н</span>
        <span>прогиб y = {ru(state.y * 100, 1)} см</span>
        <span>в равновесии y = {ru(yEq * 100, 1)} см</span>
      </div>

      <Slider label="Масса гири" value={massG} min={50} max={500} step={10} unit="г" onChange={setMassG} />
      <Slider label="Жёсткость линейки k" value={k} min={40} max={400} step={10} unit="Н/м" onChange={setK} />

      <div className="row-wrap" style={{ marginTop: 12 }}>
        <button className="btn" onClick={() => setState({ y: 0, v: 0 })}>⬇️ Положить гирю заново</button>
        <button className="btn" onClick={() => setState({ y: yEq * 1.8, v: 0 })}>👆 Придавить пальцем</button>
      </div>
      <p className="muted small">
        Деформация — причина, сила упругости — следствие. Время замедлено в {SLOW} раз.
        Сравни: тяжелее гиря или мягче линейка — прогиб больше, а колебания медленнее.
      </p>
    </div>
  )
}

